/**
 * Unified ContentService — per-namespace configuration.
 *
 * Each namespace declares how its `ContentRef.logicalKey` maps to the concrete
 * URLs used by the five source adapters (LocalDisk, Static, CloudFrontSigned,
 * S3Stream, BackendProxy). This is the single place that knows that
 *   - gallery keys look like `gallery/hiking/2024/x/photo.webp`
 *   - hospitality keys look like `hospitality-menu/{root}/{rel}`
 *   - curriculum keys are bucket-relative (`library/...`, `curriculum/...`)
 *   - fsi keys look like `fsi/{language}/{rest}`
 * etc.
 *
 * Replaces the ad-hoc URL logic spread across the legacy local-gallery modules,
 * hospitalityMenuUrls.js, curriculumSummaryAssetService.ts, setupProxy FSI/radio.
 */

import { sameOriginUrl, getContentConfig } from './config.js';

function encodePathSegments(rest) {
  return String(rest || '')
    .split('/')
    .filter(Boolean)
    .map((s) => encodeURIComponent(s))
    .join('/');
}

/** Split `hospitality-menu/{root}/{rel}` into {root, rel}. */
export function parseHospitalityKey(logicalKey) {
  const rest = logicalKey.replace(/^hospitality-menu\//, '');
  const idx = rest.indexOf('/');
  if (idx === -1) return { root: rest, rel: '' };
  return { root: rest.slice(0, idx), rel: rest.slice(idx + 1) };
}

/**
 * Backend-owned route templates — the ONLY knowledge of backend paths in the
 * frontend. They are bootstrapped from GET /api/content/namespace-config (see
 * loadBackendRoutes) so a backend route rename needs no frontend deploy. The
 * embedded BACKEND_ROUTES_FALLBACK mirrors the backend's current values and is
 * used until the manifest loads or if the backend is unreachable — the frontend
 * must keep working offline. Frontend-owned dev/static paths (/__dev/*,
 * /mgv-local-*, /data/*) are NOT part of this manifest.
 */
const BACKEND_ROUTES_FALLBACK = {
  gallery: {
    sign: '/auth/gallery/media/signed-url?key={key}',
    stream: '/api/gallery/media/stream?key={key}',
  },
  studio: {
    sign: '/auth/gallery/media/signed-url?key={key}',
    stream: '/api/gallery/media/stream?key={key}',
  },
  hospitality: {
    proxy: '/api/hospitality/menu/asset?root={root}&path={path}',
  },
  curriculum: {
    sign: '/curriculum/summary-asset-url?path={key}',
  },
  fsi: {
    sign: '/api/language/fsi/lesson-audio?key={key}',
    proxy: '/api/language/fsi/lesson-audio?key={key}',
  },
  photoAlbum: {
    sign: '/family-friends/photo-album/signed-url?key={key}',
    signThumb: '/family-friends/photo-album/thumb-signed-url?key={key}',
  },
  radio: {
    proxy: '/api/radio-stream?url={key}',
  },
};

let backendRoutes = BACKEND_ROUTES_FALLBACK;
let backendRoutesLoaded = false;
let backendRoutesLoading = null;

// REACT_APP_API_URL is a prefix that ALREADY ends in `/api` (e.g.
// https://api.mgv.gallery/api). Backend-route templates start with `/api`,
// so prefix them to route around the CloudFront `/api` 502 (see namespaceConfig).
const BACKEND_API_URL = process.env.REACT_APP_API_URL || '/api';

/** Fill a `{name}` template, URL-encoding each placeholder value. */
function fill(tpl, vars) {
  if (!tpl) return null;
  let url = String(tpl).replace(/\{(\w+)\}/g, (_, name) => encodeURIComponent(vars[name] ?? ''));
  if (url.startsWith('/api/') || url === '/api') {
    url = `${BACKEND_API_URL}${url.slice('/api'.length)}`;
  }
  return url;
}

/**
 * Merge a backend manifest into the fallback so a partial response can never
 * delete an unknown namespace or route type.
 */
function normalizeManifest(data) {
  const merged = { ...BACKEND_ROUTES_FALLBACK };
  const ns = data && data.namespaces ? data.namespaces : data;
  for (const [name, routes] of Object.entries(ns || {})) {
    if (routes && typeof routes === 'object') {
      merged[name] = { ...(BACKEND_ROUTES_FALLBACK[name] || {}), ...routes };
    }
  }
  return merged;
}

/**
 * Warn (non-fatally) when the backend manifest is missing a namespace or
 * route-type the frontend contract expects. The fallback already covers the
 * gap, but a silent drift means a backend route rename won't take effect on
 * the web client — surface it so the regression is visible in logs.
 * @param {object} data raw manifest ({ namespaces: {...} } or { name: {...} })
 */
function warnOnManifestDrift(data) {
  const ns = data && data.namespaces ? data.namespaces : data;
  if (!ns || typeof ns !== 'object') return;
  const missing = [];
  for (const [name, routes] of Object.entries(BACKEND_ROUTES_FALLBACK)) {
    if (!ns[name]) {
      missing.push(name);
      continue;
    }
    for (const routeType of Object.keys(routes)) {
      if (!ns[name][routeType]) missing.push(`${name}.${routeType}`);
    }
  }
  if (missing.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[namespaceConfig] backend /api/content/namespace-config is missing ` +
      `${missing.length} expected route template(s): ${missing.join(', ')}. ` +
      `Using embedded fallback for those — a backend route rename will NOT ` +
      `take effect until the manifest is updated.`,
    );
  }
}

/**
 * Fetch backend-owned route templates at boot. Safe to call repeatedly; the
 * first in-flight request is reused. On any failure the embedded fallback is
 * kept so content resolution never breaks.
 * @param {{fetch?: Function, force?: boolean}} [opts]
 */
export async function loadBackendRoutes(opts = {}) {
  if (backendRoutesLoaded && !opts.force) return backendRoutes;
  if (backendRoutesLoading && !opts.force) return backendRoutesLoading;
  const fetchFn = opts.fetch || (typeof fetch !== 'undefined' ? fetch : null);
  // REACT_APP_API_URL is a prefix that ALREADY ends in `/api` (see
  // config/env.js). Append the backend sub-path WITHOUT an extra `/api`.
  const API_URL = process.env.REACT_APP_API_URL || '/api';
  backendRoutesLoading = (async () => {
    if (!fetchFn) return backendRoutes;
    try {
      const res = await fetchFn(`${API_URL}/content/namespace-config`, {
        headers: { accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`namespace-config ${res.status}`);
      const data = await res.json();
      warnOnManifestDrift(data);
      backendRoutes = normalizeManifest(data);
    } catch (e) {
      // keep fallback; never break resolution
    } finally {
      backendRoutesLoaded = true;
    }
    return backendRoutes;
  })();
  try {
    return await backendRoutesLoading;
  } finally {
    backendRoutesLoading = null;
  }
}

/** Return the active backend route templates for a namespace (or null). */
export function getBackendRoutes(namespace) {
  return backendRoutes[namespace] || null;
}

/** True once loadBackendRoutes has settled (success or failure). */
export function areBackendRoutesLoaded() {
  return backendRoutesLoaded;
}

/**
 * Per-namespace descriptor.
 * Each builder receives (ref, cfg) and returns a string URL or null when the
 * source is not applicable to that namespace.
 */
export const NAMESPACE_CONFIG = {
  // Local, editable, personal tree. No adapter / backend — backed purely by
  // ContentStore (localStorage). `editable` lets the authoring UI mount;
  // `mergeUserContent:false` means it is never overlaid onto curated namespaces
  // (it is the source, not an overlay). T009.
  usercontent: { editable: true, mergeUserContent: false },

  // Local, editable, personal tree. No adapter / backend — it is backed purely
  // by ContentStore (localStorage). `editable` lets the authoring UI mount;
  // `mergeUserContent:false` means it is never overlaid onto curated namespaces
  // (it is the source, not an overlay). T009.
  usercontent: { editable: true, mergeUserContent: false },

  gallery: {
    buildDiskUrl(ref, cfg) {
      const rest = ref.logicalKey.replace(/^gallery\//, '');
      return sameOriginUrl(`mgv-local-gallery/${encodePathSegments(rest)}`, cfg);
    },
    buildStaticUrl() {
      return null; // gallery is not baked to /data in prod
    },
    buildStreamUrl(ref) {
      const r = getBackendRoutes('gallery');
      return fill(r && r.stream, { key: ref.logicalKey });
    },
    signEndpoint(ref) {
      const r = getBackendRoutes('gallery');
      return fill(r && r.sign, { key: ref.logicalKey });
    },
    buildBackendProxyUrl() {
      return null;
    },
  },

  hospitality: {
    // Browse-index URL for the content-model ContentBrowserRoute (adapter:
    // hospitalityIndexToItem). Same baked index the mobile app consumes.
    url: () => '/data/hospitality/menu-index.json',
    buildDiskUrl(ref, cfg) {
      const { root, rel } = parseHospitalityKey(ref.logicalKey);
      return `/__dev/hospitality-menu-disk?root=${encodeURIComponent(root)}&path=${encodeURIComponent(rel)}`;
    },
    buildStaticUrl(ref, cfg) {
      const { root, rel } = parseHospitalityKey(ref.logicalKey);
      return sameOriginUrl(
        `data/hospitality-menu/${encodeURIComponent(root)}/${encodePathSegments(rel)}`,
        cfg,
      );
    },
    buildStreamUrl() {
      return null;
    },
    signEndpoint() {
      return null; // hospitality prod images are static/unsigned
    },
    buildBackendProxyUrl(ref) {
      const r = getBackendRoutes('hospitality');
      if (!r || !r.proxy) return null;
      const { root, rel } = parseHospitalityKey(ref.logicalKey);
      return fill(r.proxy, { root, path: rel });
    },
  },

  curriculum: {
    // Browse-index URL for the content-model ContentBrowserRoute (adapter:
    // curriculumTreeToItem). Auth-gated backend API; same endpoint the bespoke
    // CurriculumPage/useCurriculumSubjects hook consumes today.
    url: () => '/api/auth/academics/curriculum-tree/json',
    buildDiskUrl() {
      return null;
    },
    buildStaticUrl(ref, cfg) {
      // Curriculum JSON (tree/index) is baked statically; assets are signed-only.
      if (/\.json$/i.test(ref.logicalKey)) {
        return sameOriginUrl(`data/${encodePathSegments(ref.logicalKey)}`, cfg);
      }
      return null;
    },
    buildStreamUrl() {
      return null;
    },
    signEndpoint(ref) {
      // Bucket-relative path is the key (no host, no namespace wrapper).
      const r = getBackendRoutes('curriculum');
      return fill(r && r.sign, { key: ref.logicalKey });
    },
    buildBackendProxyUrl() {
      return null;
    },
  },

  fsi: {
    buildDiskUrl(ref, cfg) {
      const rest = ref.logicalKey.replace(/^fsi\//, '');
      return sameOriginUrl(`mgv-local-fsi/${encodePathSegments(rest)}`, cfg);
    },
    buildStaticUrl() {
      return null;
    },
    buildStreamUrl(ref) {
      return `/__dev/fsi-s3?key=${encodeURIComponent(ref.logicalKey)}`;
    },
    signEndpoint(ref) {
      const r = getBackendRoutes('fsi');
      return fill(r && r.sign, { key: ref.logicalKey });
    },
    buildBackendProxyUrl(ref) {
      const r = getBackendRoutes('fsi');
      return fill(r && r.proxy, { key: ref.logicalKey });
    },
  },

  studio: {
    buildDiskUrl() {
      return null;
    },
    buildStaticUrl(ref, cfg) {
      // Studio covers are baked under /data/gallery/...
      const rest = ref.logicalKey.replace(/^studio\//, '');
      return sameOriginUrl(`data/gallery/${encodePathSegments(rest)}`, cfg);
    },
    buildStreamUrl(ref) {
      const r = getBackendRoutes('studio');
      return fill(r && r.stream, { key: ref.logicalKey });
    },
    signEndpoint(ref) {
      const r = getBackendRoutes('studio');
      return fill(r && r.sign, { key: ref.logicalKey });
    },
    buildBackendProxyUrl() {
      return null;
    },
  },

  photoAlbum: {
    // Browse-index URL for the content-model ContentBrowserRoute (adapter:
    // albumIndexToItem). The bake (public/data/photo-album-folder-index.json) is
    // a nested folder tree of albums/photos. Key is `photoAlbum` (camelCase) to
    // match the backend manifest, the resolver (src/content/resolver.js), and
    // src/content/types.js — see contentServiceBridge.namespaceForKey.
    url: () => '/data/photo-album-folder-index.json',
    buildDiskUrl() {
      return null;
    },
    buildStaticUrl() {
      return null;
    },
    buildStreamUrl() {
      return null;
    },
    signEndpoint(ref) {
      const r = getBackendRoutes('photoAlbum');
      if (!r) return null;
      const tpl = ref.variant === 'thumb' ? r.signThumb : r.sign;
      return fill(tpl, { key: ref.logicalKey });
    },
    buildBackendProxyUrl() {
      return null;
    },
  },

  sheetmusic: {
    // Browse-index URL for the content-model ContentBrowserRoute (adapter:
    // sheetmusicListToItem). The bake (sheetmusic/songs.json) is an array of
    // fake-book score objects (title/performer/genre/mp3[]/sheet[]/midi/mxl).
    url: () => '/sheetmusic/songs.json',
    buildDiskUrl() {
      return null;
    },
    buildStaticUrl() {
      return null;
    },
    buildStreamUrl() {
      return null;
    },
    signEndpoint() {
      return null;
    },
    buildBackendProxyUrl() {
      return null;
    },
  },

  radio: {
    // Browse-index URL for the content-model ContentBrowserRoute (adapter:
    // radioTopicsToItem). Single source of truth for topic groups — same
    // endpoint web/mobile/admin sync from (falls back to embedded constants
    // in studioRadioTopics.js when unreachable).
    url: () => '/api/radio-browser/topics',
    buildDiskUrl() {
      return null;
    },
    buildStaticUrl() {
      return null;
    },
    buildStreamUrl() {
      return null;
    },
    signEndpoint() {
      return null;
    },
    buildBackendProxyUrl(ref) {
      // ref.logicalKey for radio is the upstream stream URL.
      const r = getBackendRoutes('radio');
      return fill(r && r.proxy, { key: ref.logicalKey });
    },
  },

  kb: {
    buildDiskUrl() {
      return null;
    },
    buildStaticUrl(ref, cfg) {
      return sameOriginUrl(`data/knowledge-base/${encodePathSegments(ref.logicalKey)}`, cfg);
    },
    buildStreamUrl() {
      return null;
    },
    signEndpoint() {
      return null;
    },
    buildBackendProxyUrl() {
      return null;
    },
  },

  // Crafts catalog — progressive-adopted onto the canonical content-model.
  // The index is generated by scripts/content-model/build-crafts-index.mjs
  // from src/context/items/crafts.js and baked to /data/crafts/index.json.
  // Only the product categories (woodworks / leatherworks / bath&body) are
  // included; the DIY-library + service pages keep their bespoke routes.
  crafts: {
    url: () => '/data/crafts/index.json',
  },

  // Studio Theater — progressive-adopted onto the canonical content-model.
  // The index is baked to /data/gallery/studio-theater/index.json and carries
  // { sections, collections, movies }. Movie covers are CDN-baked (coverUrl),
  // playback uses the gallery stream proxy (videoKey + stream:true).
  'gallery-theater': {
    url: () => '/data/gallery/studio-theater/index.json',
  },

  // Knowledge Base — progressive-adopted onto the canonical content-model.
  // The index is compiled by scripts/knowledge-base/compile-vault.mjs and baked
  // to /data/knowledge-base/index.json ({ subjects: {id: {name, pageCount,
  // conceptCount}}, concepts, pages }). Per-subject/concept JSON is fetched by
  // the `kb` namespace above.
  'knowledge-base': {
    url: () => '/data/knowledge-base/index.json',
  },
};

export function getNamespaceConfig(namespace) {
  const cfg = NAMESPACE_CONFIG[namespace];
  if (!cfg) return null;
  // T009: default opt-in flags so every namespace can be queried uniformly.
  // A curated namespace flips `mergeUserContent:true` to allow the user's
  // `usercontent` tree to overlay at read time (US4). `usercontent` itself is
  // always the overlay SOURCE, so it stays false.
  return {
    ...cfg,
    editable: cfg.editable ?? false,
    mergeUserContent: cfg.mergeUserContent ?? false,
  };
}

/** Convenience: build the sign endpoint URL for a ref (relative path or null). */
export function signEndpointFor(namespace, ref) {
  const cfg = getNamespaceConfig(namespace);
  if (!cfg || typeof cfg.signEndpoint !== 'function') return null;
  return cfg.signEndpoint(ref, getContentConfig());
}

/**
 * Namespaces whose PRIMARY resolution requires a signed (CloudFront) URL.
 * Co-located with NAMESPACE_CONFIG so ContentService's routing policy stays in
 * one place — callers (incl. the content-model bridge) must not re-derive it.
 */
export const SIGNED_NAMESPACES = new Set([
  'gallery',
  'fsi',
  'photoAlbum',
]);

/** Every namespace ContentService understands. */
export const ALL_NAMESPACES = Object.keys(NAMESPACE_CONFIG);

/**
 * Infer the ContentService namespace from a raw storage key prefix.
 * `nsHint` always wins — used when a key is ambiguous (e.g. bucket-relative
 * photo-album keys look like curriculum keys). Mirrors the key shapes the
 * per-namespace builders above expect.
 */
export function namespaceForKey(key, nsHint) {
  if (nsHint && NAMESPACE_CONFIG[nsHint]) return nsHint;
  const k = String(key || '');
  if (/^https?:\/\//i.test(k)) return 'radio';
  if (k.startsWith('gallery/')) return 'gallery';
  if (k.startsWith('hospitality-menu/')) return 'hospitality';
  if (k.startsWith('fsi/')) return 'fsi';
  if (k.startsWith('studio/')) return 'studio';
  if (k.startsWith('knowledge-base/')) return 'kb';
  if (k.startsWith('library/') || k.startsWith('curriculum/')) return 'curriculum';
  return 'photoAlbum';
}

function contentTypeForKind(kind) {
  if (kind === 'video') return 'video';
  if (kind === 'audio') return 'audio';
  if (kind === 'pdf') return 'pdf';
  return 'image';
}

/**
 * Build a ContentRef from a raw storage key. Single factory mapping a key's
 * shape to a namespace + signing policy, so no caller re-encodes ContentService
 * routing. (The resolver decides the actual source chain from the namespace;
 * `requireSigned` here is metadata for callers that pre-warm the cache.)
 */
export function refFromKey(key, opts = {}) {
  const ns = namespaceForKey(key, opts.namespace);
  const raw = String(key || '');
  const isJson = /\.json$/i.test(raw);
  const logicalKey = ns === 'radio' ? raw : raw.replace(/^\/+/, '');
  return {
    namespace: ns,
    logicalKey,
    contentType: isJson ? 'json' : contentTypeForKind(opts.kind),
    variant: opts.variant || (opts.kind === 'image' ? 'thumb' : 'full'),
    requireSigned: SIGNED_NAMESPACES.has(ns) && !isJson,
  };
}
